/**
 * KT Surfing - Region & Market Selector Modal JS
 * Security-hardened client module handling location detection, location mapping (EU/UK/US),
 * cookie persistence, open-redirect prevention, Shopify Customer Privacy API compatibility,
 * and Privacy Banner layering suppression.
 */

class KtRegionModal {
  static COOKIE_NAME = 'kt_store_region';
  static COOKIE_MAX_AGE = 31536000; // 1 year in seconds
  static ALLOWED_REGIONS = ['FR', 'DE', 'PT', 'ES', 'EU', 'US', 'OTHER'];
  static ALLOWED_DOMAINS = ['ktsurfing.com', 'eu.ktsurfing.com'];

  // Full list of European ISO country codes (including UK / GB)
  static EU_COUNTRIES = [
    'AL','AD','AT','BE','BA','BG','HR','CY','CZ','DK','EE','FI','FR','DE',
    'GR','HU','IS','IE','IT','LV','LI','LT','LU','MT','MC','ME','NL','MK',
    'NO','PL','PT','RO','SM','RS','SK','SI','ES','SE','CH','GB','UK','VA'
  ];

  constructor() {
    this.modalElem = document.getElementById('KtRegionModal');
    if (!this.modalElem) return;

    this.detectedCountry = this.sanitizeRegion(this.modalElem.dataset.detectedCountry);
    this.currentHost = window.location.hostname;
    this.options = this.modalElem.querySelectorAll('.kt-region-modal__option');
    this.isCliPreview = this.checkIsCliPreview();

    this.init();
  }

  init() {
    this.initPrivacyApi();

    const urlParams = new URLSearchParams(window.location.search);
    const forceShow = urlParams.has('change_region') || urlParams.has('region_modal');
    
    // 1. Cross-Domain URL Transfer Check (Sanitized & Validated)
    const rawIncomingRegion = urlParams.get('kt_region');
    const incomingRegion = this.sanitizeRegion(rawIncomingRegion);

    if (incomingRegion && KtRegionModal.ALLOWED_REGIONS.includes(incomingRegion)) {
      this.setCookie(KtRegionModal.COOKIE_NAME, incomingRegion, KtRegionModal.COOKIE_MAX_AGE);
      this.cleanUrlParam('kt_region');
    }

    const savedRegion = this.sanitizeRegion(this.getCookie(KtRegionModal.COOKIE_NAME));

    // Identify & highlight visitor's region (initial check + dynamic IP check via Shopify endpoint)
    const activeCountry = this.getVisitorCountry();
    this.highlightDetectedCountry(activeCountry);
    this.detectRealIpCountry();

    // Bind button click listeners
    this.options.forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleSelection(e.currentTarget));
    });

    // Auto-show modal if no valid saved preference exists AND not coming from a cross-domain parameter
    if ((!savedRegion && !incomingRegion) || forceShow) {
      this.show();
    }
  }

  async detectRealIpCountry() {
    try {
      // Query Shopify's native edge browsing context endpoint for accurate IP geolocation
      const response = await fetch('/browsing_context_suggestions.json', { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        const rawCountry = data?.detected_values?.country?.handle || data?.detected_values?.country?.iso_code;
        if (rawCountry) {
          const detectedIpCountry = this.sanitizeRegion(rawCountry);
          this.highlightDetectedCountry(detectedIpCountry);
        }
      }
    } catch (e) {
      // Safe fallback if endpoint is unavailable
    }
  }

  sanitizeRegion(val) {
    if (!val || typeof val !== 'string') return '';
    const clean = val.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '');
    return clean.substring(0, 10);
  }

  checkIsCliPreview() {
    const host = window.location.hostname;
    const isDevHost = host.includes('myshopify.com') || host.includes('localhost') || host === '127.0.0.1' || host.includes('shopifypreview.com');
    const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    return Boolean(isDevHost || isDesignMode);
  }

  getVisitorCountry() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.getRegion === 'function') {
        const privacyRegion = window.Shopify.customerPrivacy.getRegion();
        if (privacyRegion) return this.sanitizeRegion(privacyRegion);
      }
    } catch (e) {
      // Safe fallback
    }
    return this.detectedCountry;
  }

  /**
   * Maps raw ISO country code (e.g. GB, IT, NL, US) to corresponding modal option button.
   */
  resolveTargetRegionButton(countryCode) {
    if (!countryCode) return null;

    // 1. Direct match (e.g. FR, DE, PT, ES, US)
    let targetButton = Array.from(this.options).find(btn => this.sanitizeRegion(btn.dataset.region) === countryCode);
    if (targetButton) return targetButton;

    // 2. European country fallback (e.g. GB / UK, IT, NL, IE, SE -> EU "Europe")
    if (KtRegionModal.EU_COUNTRIES.includes(countryCode)) {
      targetButton = Array.from(this.options).find(btn => this.sanitizeRegion(btn.dataset.region) === 'EU');
      if (targetButton) return targetButton;
    }

    // 3. All other countries fallback -> "OTHER"
    targetButton = Array.from(this.options).find(btn => this.sanitizeRegion(btn.dataset.region) === 'OTHER');
    return targetButton || null;
  }

  highlightDetectedCountry(countryCode) {
    if (!countryCode) return;

    // Clear any previous highlights
    this.options.forEach((btn) => {
      btn.classList.remove('kt-region-modal__option--detected');
      const badge = btn.querySelector('.kt-region-modal__detected-badge');
      if (badge) badge.hidden = true;
    });

    const targetButton = this.resolveTargetRegionButton(countryCode);
    if (targetButton) {
      targetButton.classList.add('kt-region-modal__option--detected');
      const badge = targetButton.querySelector('.kt-region-modal__detected-badge');
      if (badge) badge.hidden = false;
    }
  }

  show() {
    this.modalElem.hidden = false;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('kt-region-modal-open');
    this.suppressPrivacyBanner();
  }

  hide() {
    this.modalElem.hidden = true;
    document.body.style.overflow = '';
    document.body.classList.remove('kt-region-modal-open');
    this.restorePrivacyBanner();
  }

  suppressPrivacyBanner() {
    const banners = document.querySelectorAll('#shopify-privacy-banner-wrapper, #shopify-privacy-banner, shopify-privacy-banner, [id*="shopify-privacy"], [class*="shopify-privacy"], [id*="shopify-pc"], [class*="shopify-pc"], .shopify-policy-banner');
    banners.forEach(b => {
      b.style.setProperty('display', 'none', 'important');
      b.style.setProperty('visibility', 'hidden', 'important');
    });

    if (!this.privacyObserver) {
      this.privacyObserver = new MutationObserver(() => {
        if (document.body.classList.contains('kt-region-modal-open')) {
          const dynamicallyAdded = document.querySelectorAll('#shopify-privacy-banner-wrapper, #shopify-privacy-banner, shopify-privacy-banner, [id*="shopify-privacy"], [class*="shopify-privacy"], [id*="shopify-pc"], [class*="shopify-pc"], .shopify-policy-banner');
          dynamicallyAdded.forEach(b => {
            b.style.setProperty('display', 'none', 'important');
            b.style.setProperty('visibility', 'hidden', 'important');
          });
        }
      });
      this.privacyObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  restorePrivacyBanner() {
    if (this.privacyObserver) {
      this.privacyObserver.disconnect();
      this.privacyObserver = null;
    }
    const banners = document.querySelectorAll('#shopify-privacy-banner-wrapper, #shopify-privacy-banner, shopify-privacy-banner, [id*="shopify-privacy"], [class*="shopify-privacy"], [id*="shopify-pc"], [class*="shopify-pc"], .shopify-policy-banner');
    banners.forEach(b => {
      b.style.removeProperty('display');
      b.style.removeProperty('visibility');
    });
  }

  handleSelection(buttonElem) {
    const rawRegion = buttonElem.dataset.region;
    const selectedRegion = this.sanitizeRegion(rawRegion);
    const targetDomain = buttonElem.dataset.targetDomain;
    const marketParam = buttonElem.dataset.marketParam;

    // Security Check: Prevent Open Redirect Vulnerability
    if (!this.isValidTargetDomain(targetDomain)) {
      console.error('[Security Exception] Invalid target domain attempted:', targetDomain);
      return;
    }

    // 1. Save region preference in cookie (shared across .ktsurfing.com)
    if (KtRegionModal.ALLOWED_REGIONS.includes(selectedRegion)) {
      this.setCookie(KtRegionModal.COOKIE_NAME, selectedRegion, KtRegionModal.COOKIE_MAX_AGE);
    }

    // 2. CLI / Shopify Dev Preview Mode Handling
    if (this.isCliPreview) {
      const currentSearchParams = new URLSearchParams(window.location.search);
      const isAlreadyOnMarket = !marketParam || currentSearchParams.get('country') === marketParam.replace('country=', '');

      if (isAlreadyOnMarket) {
        // Already on target market in CLI, just close modal smoothly
        this.hide();
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('change_region');
      searchParams.delete('region_modal');
      searchParams.set('kt_region', selectedRegion);

      if (marketParam) {
        const [paramKey, paramVal] = marketParam.split('=');
        if (paramKey && paramVal) {
          searchParams.set(encodeURIComponent(paramKey), encodeURIComponent(paramVal));
        }
      }

      window.location.search = searchParams.toString();
      return;
    }

    // 3. Production Routing:
    const isSameDomain = (this.currentHost === targetDomain);
    const currentSearchParams = new URLSearchParams(window.location.search);
    const isAlreadyOnMarket = !marketParam || currentSearchParams.get('country') === marketParam.replace('country=', '');

    if (isSameDomain && isAlreadyOnMarket) {
      // Already on correct domain & market, close modal smoothly without reloading
      this.hide();
      return;
    }

    const currentPath = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    searchParams.delete('change_region');
    searchParams.delete('region_modal');

    // Attach market parameter (e.g. country=FR)
    if (marketParam) {
      const [paramKey, paramVal] = marketParam.split('=');
      if (paramKey && paramVal) {
        searchParams.set(encodeURIComponent(paramKey), encodeURIComponent(paramVal));
      }
    }

    // Attach cross-domain region parameter to guarantee sync on target site
    searchParams.set('kt_region', selectedRegion);

    const newQueryString = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const targetUrl = `https://${targetDomain}${currentPath}${newQueryString}`;

    window.location.href = targetUrl;
  }

  isValidTargetDomain(domain) {
    if (!domain || typeof domain !== 'string') return false;
    if (this.isCliPreview) return true; // Allow CLI host testing
    return KtRegionModal.ALLOWED_DOMAINS.includes(domain.toLowerCase());
  }

  initPrivacyApi() {
    document.addEventListener('visitorConsentCollected', () => {
      // Privacy consent listener
    });
  }

  setCookie(name, value, maxAgeSeconds) {
    const isHttps = window.location.protocol === 'https:';
    const secureFlag = isHttps ? '; Secure' : '';

    let domainScope = '';
    if (this.currentHost.endsWith('ktsurfing.com')) {
      domainScope = '; domain=.ktsurfing.com';
    }

    const cleanValue = encodeURIComponent(value);
    document.cookie = `${name}=${cleanValue}; path=/${domainScope}; max-age=${maxAgeSeconds}; SameSite=Lax${secureFlag}`;
  }

  getCookie(name) {
    const matches = document.cookie.match(new RegExp(
      '(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'
    ));
    return matches ? decodeURIComponent(matches[1]) : null;
  }

  cleanUrlParam(paramKey) {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete(paramKey);
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new KtRegionModal();
});
